import Vue from 'vue'
import { Client } from '../client/'
import MarketingCookie from '../client/MarketingCookie'
import { Notifications } from '../notification/'
import router from '@/modules/router'
import store from '@/modules/store'
import qs from 'qs'
import mixins from '@/modules/mixins/global'
import isEmpty from 'lodash.isempty'
import isEqual from 'lodash.isequal'
import EventEmitter from 'events'

Vue.prototype.$refreshEndpoint = '/auth/refresh'

/**
 * Authentication class
 *
 * Handles authentication, logging in and logging out, also stores the currently logged-in users instance
 */
class User {
    constructor() {
        this.client = new Client()
        this.notification = new Notifications(Vue.prototype.$isSupported)
        this._event = new EventEmitter()
        this.apiRoot = 'api'
        this.apiVersion = 'v1'
        this._model = this.client.dataStorage.user
        this._isLoggedIn = Boolean(this.client.dataStorage.accessToken)
    }

    /**
     * Initialize at entrypoint
     *
     */
    async initialize() {
        try {
            if (!this._isLoggedIn) await this.refreshToken()
            await this.set()
            return
        } catch {
            this.reset()
            return
        }
    }

    #expenseRuleParams

    /**
     * Logs in a user
     *
     * @param {object} data The users credentials ex.: {email: 'something@example.com', password: 'example'}
     * @returns {Promise} the login request
     */
    async login(data) {
        try {
            this.reset()
            const tokens = await this.client.store(`${this.base}/auth/login`, data)
            this.storeTokens(tokens)
            const getExpenseRules = this.getExpenseRules()
            const getMe = this.getMe()
            await Promise.all([getExpenseRules, getMe])
                .then(() => Promise.resolve())
                .catch((e) => Promise.reject(e))
        } catch (e) {
            return Promise.reject(e)
        }
    }
    /**
     * Register a user
     *
     * @param {object} data credentials
     * @returns {Promise} the register request
     */
    register(data) {
        return this.client.store(`${this.base}/auth/register`, data)
    }
    /**
     * Company invite (tasks#79): both calls go to core-api — the paths are not in AppOrdit.js.
     *
     * @param {string} token invite token from the e-mail link
     * @returns {Promise} invite details for the accept form (404 → rejects with the body)
     */
    getInvite(token) {
        return this.client.index(`${this.base}/company-invites/token/${encodeURIComponent(token)}`)
    }
    /**
     * @param {object} payload { token, firstName, lastName, phone, plainPassword,
     *                           acceptedTermsVersion, acceptedPrivacyVersion, isSubscribedForNewsletter }
     * @returns {Promise} { token, refresh_token } (400 → rejects with { errors: { field: [msg] } })
     */
    acceptInvite(payload) {
        return this.client.store(`${this.base}/company-invites/accept`, payload)
    }
    /**
     * Update a user
     *
     * @param {object} data The user object.
     * @returns {Promise} the updated user
     */
    async update(data) {
        this.model = await this.client.update(`${this.base}/users/${data.id}`, data)
        return this.model
    }

    /**
     * Logs out a user
     *
     * Resets the token dataStorage, and leaves any private subscribed channels
     */
    async logout(withRedirect = true) {
        try {
            await store.dispatch('order/resetAddress')
            await store.dispatch('active/resetCanteen')
            await store.dispatch('canteen/getResidentialList')
            this.reset()
            await Vue.prototype.$cart.resetGroupOrder()
            Vue.prototype.$cart.resetAllowance()
            Vue.prototype.$cart.resetCart()
            Vue.prototype.$user.getExpenseRules()
            Vue.prototype.$addresses.reset()
            this.event('logout')
            if (withRedirect && !['restaurant-list', 'reset'].includes(router.currentRoute.name)) {
                router.push({ name: 'restaurant-list' })
            }
            return Promise.resolve()
        } catch (e) {
            return Promise.reject(e)
        }
    }

    /**
     * Send password reset email
     *
     */

    async sendResetEmail(email) {
        try {
            const res = await this.client.store(`${this.base}/auth/resetting`, {
                email: email || this.model.email,
            })
            if (res.code === 202) {
                store.dispatch('toastInfo', {
                    title: Vue.i18n.translate('login.new_psw_info.title'),
                    text: Vue.i18n.translate('login.new_psw_info.info_text', {
                        date: Vue.options.filters.toDateTime(res.message),
                    }),
                })
            } else {
                store.dispatch('toastSuccess', {
                    title: Vue.i18n.translate('login.new_psw_info.title'),
                    text: Vue.i18n.translate('login.new_psw_info.text', {
                        email: email || this.model.email,
                    }),
                })
            }
            return Promise.resolve(res)
        } catch (e) {
            return Promise.reject(e)
        }
    }

    /**
     * Reset password
     *
     */

    async resetPassword(payload) {
        try {
            const res = await this.client.store(`${this.base}/auth/reset`, {
                token: payload.token,
                plain_password: payload.plain_password,
            })
            store.dispatch('toastSuccess', {
                title: Vue.i18n.translate('base.success'),
                text: Vue.i18n.translate('login.new_psw_success'),
            })
            return Promise.resolve(res)
        } catch (e) {
            return Promise.reject(e)
        }
    }
    /**
     * Activate email address
     *
     */

    async activate(payload) {
        try {
            const res = await this.client.store(`${this.base}/auth/activate`, {
                email: payload.email,
            })
            store.dispatch('toastSuccess', {
                title: Vue.i18n.translate('base.success'),
                text: Vue.i18n.translate('labels.activate_info'),
            })
            return Promise.resolve(res)
        } catch (e) {
            return Promise.reject(e)
        }
    }

    /**
     *  Get me
     */

    async getMe() {
        if (!this.client.dataStorage.accessToken) return Promise.resolve()
        try {
            this.model = await this.client.index(`${this.base}/me`, {
                headers: {
                    Authorization: this.client.dataStorage.headers,
                },
            })
            this._isLoggedIn = true
            MarketingCookie.sync()
            return Promise.resolve()
        } catch (e) {
            return Promise.reject(e)
        }
    }

    /**
     *  Facebook authentication
     */

    facebookAuth() {
        store.getters(['site/config'])
        const loc = window.location
        const url = `${loc.protocol}//${loc.host}/login${loc.search}`
        window.location.href = `/connect/facebook?_destination=${url}`
    }

    /**
     *  Token handling
     */

    async refreshToken(refreshToken) {
        try {
            if (!refreshToken && !this.client.dataStorage.refreshToken)
                return Promise.reject('refresh token not found')
            const res = await this.client.store(`${this.base}${Vue.prototype.$refreshEndpoint}`, {
                refresh_token: refreshToken || this.client.dataStorage.refreshToken,
            })
            this.storeTokens(res)
            return Promise.resolve(res)
        } catch (e) {
            return Promise.reject(e)
        }
    }

    async autoRefreshToken(config) {
        try {
            await this.refreshToken()
            await this.update(this.model)
            const res = await this.client.request(config.url, config.options)
            return Promise.resolve(res)
        } catch (e) {
            this.reset()
            return Promise.reject(e)
        }
    }

    storeTokens(data) {
        this.client.dataStorage.accessToken = data.token
        this.client.dataStorage.refreshToken = data.refresh_token
        this.client.dataStorage.tokenType = 'Bearer'
    }

    reset() {
        this.model = {}
        this.client.dataStorage.user = undefined
        this.client.dataStorage.accessToken = undefined
        this.client.dataStorage.refreshToken = undefined
        this.client.dataStorage.tokenType = undefined
        this._isLoggedIn = false
        MarketingCookie.clear()
    }

    /**
     * Set current address
     *
     * @param {number} id Address id
     * @returns {Promise} the update request
     */
    setCurrentAddress(id) {
        if (!this.isLoggedIn) return Promise.resolve()
        if (!id) return this.client.store('frontend-api/user/clear-current-address')
        if (`/api/v1/user-addresses/${id}` !== this.model.current_address)
            return this.update({
                ...this.model,
                ...{ current_address: `/api/v1/user-addresses/${id}` },
            })
        return Promise.resolve()
    }

    setAddressPositions(positions) {
        if (!this.isLoggedIn) return Promise.resolve()
        return this.client.store('frontend-api/user/address-position', {
            positions,
        })
    }

    async set() {
        try {
            Vue.nextTick(() => {
                if (this.notification.notificationsAllowed) this.notification.register()
            })
            await Promise.all([this.getExpenseRules(), this.getMe()])
            return Promise.resolve()
        } catch {
            return Promise.reject()
        }
    }

    async getExpenseRules(force = false) {
        if (!this.isLoggedIn) return Promise.resolve()
        let canteen = store.getters['active/canteen']
        let params = qs.stringify(
            {
                delivery: !isEmpty(canteen)
                    ? canteen.delivery_time
                    : store.getters['order/time']
                    ? new Date(store.getters['order/time'])
                    : undefined,
                restaurant_id: store.getters['active/restaurant']?.id || undefined,
                address_id: !isEmpty(canteen) // for the error messages
                    ? canteen.isResidential
                        ? canteen.address?.user_address
                            ? mixins.getId(canteen.address?.user_address)
                            : undefined
                        : mixins.getId(canteen.address)
                    : store.getters['order/address']?.id || undefined,
            },
            {
                encode: true,
                arrayFormat: Vue.prototype.$arrayFormat,
            }
        )
        if (isEqual(this.#expenseRuleParams, params) && !force) return Promise.resolve()
        this.#expenseRuleParams = params
        params = params !== '' ? `?${params}` : ''
        try {
            const res = await this.client.index(`${this.base}/company-expense-rules${params}`)
            await store.dispatch('setExpenseRules', res.data)
            await store.dispatch('expenseRulesLoaded')
            MarketingCookie.sync()
            return Promise.resolve()
        } catch {
            await store.dispatch('expenseRulesLoaded')
            return Promise.reject()
        }
    }

    /**
     * URL base getter
     */

    get base() {
        return `${this.apiRoot}/${this.apiVersion}`
    }

    /**
     * User getter + setter
     */

    get model() {
        return this._model
    }

    set model(user) {
        this.client.dataStorage.user = user
        this._model = user
    }

    /**
     * Getters
     */

    get name() {
        return store.getters['locale'] === 'hu'
            ? `${this._model.last_name} ${this._model.first_name}`
            : `${this._model.first_name} ${this._model.last_name}`
    }

    get canteens() {
        return this.model?.company?.cloudcanteens || []
    }

    get onlyCloudCanteen() {
        return this.model?.company?.is_only_cloud_canteen_enabled || false
    }

    get isLoggedIn() {
        return this._isLoggedIn
    }

    get authExpired() {
        const timeLeft = new Date(parseInt(this.client.dataStorage.expiresAt || 0)) - new Date()
        return (
            this._isLoggedIn &&
            !(
                timeLeft > 5 * 60 * 1000 && // 5 minute
                Boolean(this.client.dataStorage.accessToken) &&
                Boolean(this.client.dataStorage.refreshToken)
            )
        )
    }

    /**
     * Authorization header for event handling
     */

    get authorizationHeader() {
        return { Authorization: this.client.dataStorage.headers }
    }

    /**
     * Class event handling
     */

    event(event) {
        if (event) this._event.emit(event)
    }

    on(event, listener) {
        if (event && listener) this._event.on(event, listener)
    }

    off(event, listener) {
        if (event && listener) this._event.off(event, listener)
    }
}

export default User
